// Menu framework (RALLY_GOAL.md §13): a stack of DOM screens that work with the mouse, the keyboard
// alone (arrows move, left/right change a value, Enter selects, Esc goes back) and a gamepad
// (D-pad/left stick, A selects, B back, Start).

export type NavInput = 'up' | 'down' | 'left' | 'right' | 'ok' | 'back' | 'start';

export interface Screen {
  el: HTMLElement;
  /** Esc / B. Default: pop the screen. */
  onBack?: () => void;
  /** Start button on a gamepad. Default: same as ok. */
  onStart?: () => void;
  /** Element to focus first. */
  initial?: HTMLElement | null;
}

const adjusters = new WeakMap<Element, (dir: -1 | 1) => void>();

export function h<K extends keyof HTMLElementTagNameMap>(tag: K, cls = '', text?: string, parent?: HTMLElement): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text !== undefined) e.textContent = text;
  parent?.appendChild(e);
  return e;
}

export function button(label: string, onClick: () => void, cls = '', parent?: HTMLElement): HTMLButtonElement {
  const b = h('button', `btn ${cls}`.trim(), undefined, parent);
  b.type = 'button';
  b.dataset.nav = '';
  h('span', '', label, b);
  b.addEventListener('click', () => onClick());
  return b;
}

/** A labelled row whose value steps through options (click halves, or left/right keys). */
export function stepper<T>(parent: HTMLElement, label: string, options: { value: T; label: string }[], get: () => T, set: (v: T) => void, hint?: string): HTMLElement {
  const row = h('div', 'row', undefined, parent);
  h('div', 'row-label', label, row);
  const ctl = h('button', 'ctl stepper', undefined, row);
  ctl.type = 'button';
  ctl.dataset.nav = '';
  if (hint) ctl.title = hint;
  const prev = h('span', 'arrow', '‹', ctl);
  const val = h('span', 'val', '', ctl);
  const next = h('span', 'arrow', '›', ctl);
  const idx = () => Math.max(0, options.findIndex((o) => o.value === get()));
  const render = () => (val.textContent = options[idx()].label);
  const step = (d: -1 | 1) => {
    const i = (idx() + d + options.length) % options.length;
    set(options[i].value);
    render();
  };
  adjusters.set(ctl, step);
  ctl.addEventListener('click', (e) => {
    const r = ctl.getBoundingClientRect();
    step(e.detail === 0 || (e as MouseEvent).clientX > r.left + r.width * 0.35 ? 1 : -1);
  });
  void prev;
  void next;
  render();
  (row as unknown as { refresh: () => void }).refresh = render;
  return row;
}

export function toggle(parent: HTMLElement, label: string, get: () => boolean, set: (v: boolean) => void): HTMLElement {
  return stepper(parent, label, [{ value: false, label: 'OFF' }, { value: true, label: 'ON' }], get, set);
}

/** A slider row: left/right keys step it, pointer drags set it. */
export function slider(parent: HTMLElement, label: string, min: number, max: number, step: number, get: () => number, set: (v: number) => void, fmt: (v: number) => string): HTMLElement {
  const row = h('div', 'row', undefined, parent);
  h('div', 'row-label', label, row);
  const ctl = h('div', 'ctl slider', undefined, row);
  ctl.tabIndex = 0;
  ctl.dataset.nav = '';
  ctl.setAttribute('role', 'slider');
  ctl.setAttribute('aria-label', label);
  const track = h('div', 'track', undefined, ctl);
  const fill = h('div', 'fill', undefined, track);
  const val = h('span', 'val', '', ctl);
  const render = () => {
    const v = get();
    fill.style.transform = `scaleX(${(v - min) / (max - min)})`;
    val.textContent = fmt(v);
    ctl.setAttribute('aria-valuenow', String(v));
  };
  (row as unknown as { refresh: () => void }).refresh = render;
  const setV = (v: number) => {
    const q = Math.round((Math.min(max, Math.max(min, v)) - min) / step) * step + min;
    set(Number(q.toFixed(4)));
    render();
  };
  adjusters.set(ctl, (d) => setV(get() + d * step));
  const fromPointer = (e: PointerEvent) => {
    const r = track.getBoundingClientRect();
    setV(min + ((e.clientX - r.left) / r.width) * (max - min));
  };
  ctl.addEventListener('pointerdown', (e) => {
    ctl.setPointerCapture(e.pointerId);
    fromPointer(e);
  });
  ctl.addEventListener('pointermove', (e) => {
    if (ctl.hasPointerCapture(e.pointerId)) fromPointer(e);
  });
  render();
  return row;
}

/** Re-render every stepper inside an element (after a preset changed several values). */
export function refreshAll(root: HTMLElement): void {
  for (const r of root.querySelectorAll('.row')) (r as unknown as { refresh?: () => void }).refresh?.();
}

export class Screens {
  readonly root: HTMLDivElement;
  private readonly stack: Screen[] = [];
  /** Called when the stack empties or changes (the game pauses input while a screen is open). */
  onChange: ((open: boolean) => void) | null = null;
  /** Key capture for remapping: the next key goes here instead of to menu navigation. */
  private keyCapture: ((code: string | null) => void) | null = null;
  /** A controller capture is running: menu navigation from the gamepad pauses. */
  capturing = false;

  /** Hand the next key press to `cb` (Escape cancels with null). */
  captureKey(cb: (code: string | null) => void): void {
    this.keyCapture = cb;
  }

  constructor(parent: HTMLElement) {
    this.root = h('div', 'screens', undefined, parent);
    window.addEventListener('keydown', (e) => this.onKey(e), true);
  }

  get open(): boolean {
    return this.stack.length > 0;
  }

  get top(): Screen | null {
    return this.stack[this.stack.length - 1] ?? null;
  }

  push(s: Screen): void {
    const prev = this.top;
    if (prev) prev.el.classList.add('hidden');
    this.stack.push(s);
    s.el.classList.add('screen');
    this.root.appendChild(s.el);
    this.focusInitial(s);
    document.body.classList.add('screen-open');
    this.onChange?.(true);
  }

  /** Replace the whole stack with one screen (or nothing). */
  set(s: Screen | null): void {
    for (const x of this.stack.splice(0)) x.el.remove();
    if (s) this.push(s);
    else {
      document.body.classList.remove('screen-open');
      this.onChange?.(false);
    }
  }

  pop(): void {
    const s = this.stack.pop();
    s?.el.remove();
    const t = this.top;
    if (t) {
      t.el.classList.remove('hidden');
      refreshAll(t.el);
      this.focusInitial(t);
    }
    document.body.classList.toggle('screen-open', this.open);
    this.onChange?.(this.open);
  }

  private focusInitial(s: Screen): void {
    const f = s.initial ?? this.items(s)[0];
    f?.focus({ preventScroll: false });
  }

  private items(s: Screen): HTMLElement[] {
    return Array.from(s.el.querySelectorAll<HTMLElement>('[data-nav]')).filter((e) => e.offsetParent !== null && !(e as HTMLButtonElement).disabled);
  }

  /** Keyboard, gamepad and test input for the top screen. */
  input(k: NavInput): void {
    const s = this.top;
    if (!s) return;
    const items = this.items(s);
    const cur = document.activeElement as HTMLElement | null;
    const i = cur ? items.indexOf(cur) : -1;
    switch (k) {
      case 'up':
      case 'down': {
        if (!items.length) return;
        const n = i < 0 ? 0 : (i + (k === 'down' ? 1 : -1) + items.length) % items.length;
        items[n].focus();
        items[n].scrollIntoView({ block: 'nearest' });
        return;
      }
      case 'left':
      case 'right': {
        const adj = cur ? adjusters.get(cur) : undefined;
        if (adj) adj(k === 'right' ? 1 : -1);
        else if (cur && cur.closest('.btn-row')) {
          // Buttons side by side: left/right moves between them.
          const row = Array.from(cur.closest('.btn-row')!.querySelectorAll<HTMLElement>('[data-nav]'));
          const j = row.indexOf(cur) + (k === 'right' ? 1 : -1);
          row[(j + row.length) % row.length]?.focus();
        }
        return;
      }
      case 'ok':
        if (cur && i >= 0) cur.click();
        else items[0]?.focus();
        return;
      case 'back':
        if (s.onBack) s.onBack();
        else this.pop();
        return;
      case 'start':
        if (s.onStart) s.onStart();
        else if (cur && i >= 0) cur.click();
        return;
    }
  }

  private onKey(e: KeyboardEvent): void {
    if (!this.open) return;
    if (this.keyCapture) {
      e.preventDefault();
      e.stopPropagation();
      if (e.repeat) return;
      const cb = this.keyCapture;
      this.keyCapture = null;
      cb(e.code === 'Escape' ? null : e.code);
      return;
    }
    const map: Record<string, NavInput> = {
      ArrowUp: 'up',
      ArrowDown: 'down',
      ArrowLeft: 'left',
      ArrowRight: 'right',
      Enter: 'ok',
      NumpadEnter: 'ok',
      Space: 'ok',
      Escape: 'back',
      Backspace: 'back',
      KeyW: 'up',
      KeyS: 'down',
      KeyA: 'left',
      KeyD: 'right',
    };
    const k = map[e.code];
    if (!k) return;
    e.preventDefault();
    e.stopPropagation();
    if (e.repeat && (k === 'ok' || k === 'back')) return;
    this.input(k);
  }
}

/** Gamepad menu navigation with edge detection and stick auto-repeat. */
export class PadNav {
  private prev = new Map<string, boolean>();
  private repeatAt = 0;

  poll(now: number, out: (k: NavInput) => void): void {
    const pads = typeof navigator !== 'undefined' && navigator.getGamepads ? navigator.getGamepads() : [];
    let up = false;
    let down = false;
    let left = false;
    let right = false;
    let ok = false;
    let back = false;
    let start = false;
    for (const p of pads) {
      if (!p || !p.connected) continue;
      const b = (i: number) => !!p.buttons[i]?.pressed;
      const ax = p.axes[0] ?? 0;
      const ay = p.axes[1] ?? 0;
      up ||= b(12) || ay < -0.6;
      down ||= b(13) || ay > 0.6;
      left ||= b(14) || ax < -0.6;
      right ||= b(15) || ax > 0.6;
      ok ||= b(0);
      back ||= b(1);
      start ||= b(9);
    }
    const dirs: [NavInput, boolean][] = [
      ['up', up],
      ['down', down],
      ['left', left],
      ['right', right],
    ];
    for (const [k, v] of dirs) {
      const was = this.prev.get(k) ?? false;
      if (v && (!was || now > this.repeatAt)) {
        out(k);
        this.repeatAt = now + (was ? 120 : 380);
      }
      this.prev.set(k, v);
    }
    for (const [k, v] of [
      ['ok', ok],
      ['back', back],
      ['start', start],
    ] as [NavInput, boolean][]) {
      const was = this.prev.get(k) ?? false;
      if (v && !was) out(k);
      this.prev.set(k, v);
    }
  }

  /** Forget held buttons (so a button held while a screen opens doesn't fire on it). */
  sync(): void {
    const pads = typeof navigator !== 'undefined' && navigator.getGamepads ? navigator.getGamepads() : [];
    for (const p of pads) {
      if (!p || !p.connected) continue;
      this.prev.set('ok', !!p.buttons[0]?.pressed);
      this.prev.set('back', !!p.buttons[1]?.pressed);
      this.prev.set('start', !!p.buttons[9]?.pressed);
    }
  }
}

/** Make any focusable element respond to left/right (tab bars, custom controls). */
export function adjustable(el: HTMLElement, fn: (dir: -1 | 1) => void): void {
  el.dataset.nav = '';
  if (el.tabIndex < 0) el.tabIndex = 0;
  adjusters.set(el, fn);
}
